import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  
  // 动画状态锁定
  #isAnimating = false;
  // 动画时长（毫秒）
  #animationDuration = 450;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#isAnimating) return;
    
    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;
    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer with debounce protection
   * @param {Event} [event]
   */
  toggle(event) {
    // 防抖：如果正在动画中，恢复 open 属性状态并忽略
    if (this.#isAnimating) {
      // 保持当前状态不变
      if (this.isOpen) {
        this.refs.details.setAttribute('open', '');
      } else {
        this.refs.details.removeAttribute('open');
      }
      return;
    }

    // 检查当前状态（基于 menu-open 类）
    if (this.isOpen) {
      // 当前是打开的，需要关闭
      // 浏览器已经移除了 open 属性，需要重新设置以保持动画
      this.refs.details.setAttribute('open', '');
      this.#closeDrawer();
    } else {
      // 当前是关闭的，需要打开
      // 浏览器已经设置了 open 属性
      this.#openDrawer(event);
    }
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    if (this.#isAnimating || this.isOpen) return;
    
    // 确保 open 属性存在
    this.refs.details.setAttribute('open', '');
    this.#openDrawer(event);
  }

  /**
   * Internal open logic
   * @param {Event} [event]
   */
  #openDrawer(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    // 锁定动画状态
    this.#isAnimating = true;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    
    // 使用 requestAnimationFrame 确保 open 属性已经生效
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      
      // 动画结束后解锁
      setTimeout(() => {
        this.#isAnimating = false;
        trapFocus(details);
      }, this.#animationDuration);
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    if (this.#isAnimating) return;
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    if (this.#isAnimating || !this.isOpen) return;
    this.#closeDrawer();
  }

  /**
   * Internal close logic
   */
  #closeDrawer() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    // 锁定动画状态
    this.#isAnimating = true;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    // 动画结束后重置状态
    setTimeout(() => {
      reset(details);
      
      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
      
      // 解锁动画状态
      this.#isAnimating = false;
    }, this.#animationDuration);
  }

  /**
   * Attach animationend event listeners to all animated elements
   */
  #setupAnimatedElementListeners() {
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
